import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, Upload, FileText, Image as ImageIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { OptionSelect } from "./FieldInput";
import { actionLabel } from "./Dialogs";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate } from "@/lib/api";

const STATUTS = [{ value: "actif", label: "Actif" }, { value: "suspendu", label: "Suspendu" }, { value: "inactif", label: "Inactif" }];

export function StatutDialog({ res, row, onClose, onDone }) {
  const [f, setF] = useState({ statut: null, motif: "" });
  useEffect(() => {
    if (row) setF({ statut: null, motif: "" });
  }, [row]);
  const submit = async () => {
    try {
      await api.post(`/statut/${res}/${row.id}`, f);
      toast.success("Statut mis à jour");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="statut-dialog">
        <DialogHeader>
          <DialogTitle>Changer le statut — {row?.code}</DialogTitle>
          <DialogDescription>Statut actuel : {STATUTS.find((s) => s.value === row?.statut)?.label}. Le changement est historisé.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Nouveau statut</Label><div className="mt-1"><OptionSelect options={STATUTS.filter((s) => s.value !== row?.statut)} value={f.statut} onChange={(v) => setF({ ...f, statut: v })} testId="statut-select" /></div></div>
          <div><Label>Motif *</Label><Textarea value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} data-testid="statut-motif-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.statut || f.motif.trim().length < 3} data-testid="statut-submit-button">Confirmer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PiecesJointes({ res, entityId }) {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [libelle, setLibelle] = useState("");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => {
    api.get(`/pieces/${res}/${entityId}`).then((r) => setRows(r.data)).catch(() => {});
  }, [res, entityId]);
  useEffect(() => {
    load();
  }, [load]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("libelle", libelle);
    setBusy(true);
    try {
      await api.post(`/pieces/${res}/${entityId}`, fd);
      toast.success("Pièce jointe ajoutée");
      setLibelle("");
      load();
    } catch (er) {
      toast.error(errMsg(er));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };
  const open = async (p) => {
    try {
      const r = await api.get(`/piece/${p.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (er) {
      toast.error("Fichier indisponible");
    }
  };
  const remove = async (p) => {
    try {
      await api.delete(`/piece/${p.id}`);
      toast.success("Pièce jointe retirée");
      load();
    } catch (er) {
      toast.error(errMsg(er));
    }
  };
  const editable = can(res, "modifier");
  return (
    <div className="mt-8" data-testid="pieces-jointes">
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wider flex items-center gap-2"><Paperclip className="h-3.5 w-3.5" />Pièces jointes</h4>
      {editable && (
        <div className="flex gap-2 mb-3">
          <Input className="h-9" placeholder="Libellé (ex. CNI recto)" value={libelle} onChange={(e) => setLibelle(e.target.value)} data-testid="piece-libelle-input" />
          <input ref={input} type="file" accept="application/pdf,image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="piece-file-input" />
          <Button size="sm" variant="outline" disabled={busy} onClick={() => input.current?.click()} data-testid="piece-upload-button">
            <Upload className="h-4 w-4 mr-1" />{busy ? "Envoi…" : "Ajouter"}
          </Button>
        </div>
      )}
      <div className="border rounded-md divide-y text-xs">
        {rows.map((p) => (
          <div key={p.id} className="p-2.5 flex items-center gap-2" data-testid="piece-row">
            {p.content_type === "application/pdf" ? <FileText className="h-4 w-4 text-primary" /> : <ImageIcon className="h-4 w-4 text-primary" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{p.libelle}</div>
              <div className="text-muted-foreground">{fmtDate(p.created_at)} · {p.created_by_name} · {Math.ceil(p.size / 1024)} Ko</div>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => open(p)} data-testid={`piece-open-${p.id}`}><Download className="h-3.5 w-3.5" /></Button>
            {editable && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => remove(p)} data-testid={`piece-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
          </div>
        ))}
        {!rows.length && <div className="p-3 text-muted-foreground">Aucune pièce jointe (PDF, JPG, PNG, WEBP — 10 Mo max.)</div>}
      </div>
    </div>
  );
}

export function EventList({ rows, title = "Historique des affectations", testId = "event-list" }) {
  const who = (r) => {
    const parts = [];
    if (r.tpe_id_label) parts.push(`TPE ${r.tpe_id_label}`);
    if (r.from_revendeur_id_label || r.to_revendeur_id_label) parts.push(`${r.from_revendeur_id_label || "—"} → ${r.to_revendeur_id_label || "—"}`);
    if (r.revendeur_id_label) parts.push(r.revendeur_id_label);
    if (r.from_superviseur_id_label || r.to_superviseur_id_label) parts.push(`Superviseur : ${r.from_superviseur_id_label || "—"} → ${r.to_superviseur_id_label || "—"}`);
    return parts.join(" · ");
  };
  return (
    <div className="mt-8" data-testid={testId}>
      <h4 className="mb-3 text-xs font-bold uppercase tracking-wider">{title}</h4>
      <div className="border rounded-md divide-y text-xs">
        {rows.map((r) => (
          <div key={r.id} className="p-2.5">
            <div className="flex justify-between gap-2"><span className="font-semibold">{actionLabel(r.action)}</span><span className="text-muted-foreground whitespace-nowrap">{fmtDate(r.date)}</span></div>
            <div className="text-muted-foreground mt-0.5">{who(r)}{r.detail ? ` · ${r.detail}` : ""} · {r.user_name}</div>
            {r.motif && <div className="mt-0.5">Motif : {r.motif}</div>}
          </div>
        ))}
        {!rows.length && <div className="p-3 text-muted-foreground">Aucun mouvement enregistré</div>}
      </div>
    </div>
  );
}
